import type {
  IEventAthleteStats,
  IEventAthleteStatsV3,
  IEventGameStats,
  IEventScoreStats,
  IMatchRosterLeaders,
  IResponseEventStatsRosters,
  IResponseEventStatsRostersV3,
  IResponseEventStatsScores,
  IResponseEventStatsTeams,
} from "~/types";
import type { TEventStatsFormat } from "~/videos/types/events";
import getSharedHeaders from "~/common/helpers/get-headers";
import sleep from "~/common/helpers/sleep";
import { formatAthleteStats, formatAthleteStatsV3, formatGameStats, formatGameStatsV3, formatScoreStats } from "~/videos/helpers/format-event";

// Helper function to get location and format response
async function withLocation<T, R>(fetchFn: () => Promise<T>, formatFn: (data: T, options: { isUS: boolean }) => R): Promise<R> {
  const location = await useLocation();
  const isUS = location.country === "US";
  const data = await fetchFn();
  return formatFn(data, { isUS });
}

export const getCachedEventGameStatsByIdV1 = useMemoize(
  (
    eventId: string,
    options?: {
      query: Partial<{ format: "sheet"; sheetId: number }>;
      accessToken?: string;
      locale?: string;
    }
  ): Promise<IEventGameStats> => getEventGameStatsByIdV1(eventId, options)
);

export const getCachedEventGameStatsByIdV3 = useMemoize(
  (
    eventId: string,
    options?: {
      query: Partial<{ format: TEventStatsFormat; sheetId: number }>;
      accessToken?: string;
      locale?: string;
    }
  ): Promise<IEventGameStats> => getEventGameStatsByIdV3(eventId, options)
);

export async function getEventGameStatsByIdV1(
  eventId: string,
  options?: {
    query: Partial<{ format: "sheet"; sheetId: number }>;
    accessToken?: string;
    locale?: string;
  }
): Promise<IEventGameStats> {
  if (eventId === "demo") {
    await sleep(2000);
    return withLocation(() => $fetch<IResponseEventStatsTeams>("/mocks/data/event/123/stats_game.json"), formatGameStats);
  }

  return withLocation(
    () =>
      useAPI<IResponseEventStatsTeams>(`/api/v1/events/${eventId}/statistics/teams`, {
        headers: getSharedHeaders(options),
        ...(options?.query && { query: options.query }),
      }),
    formatGameStats
  );
}

export async function getEventGameStatsByIdV3(
  eventId: string,
  options?: {
    query: Partial<{ format: TEventStatsFormat; sheetId: number }>;
    accessToken?: string;
    locale?: string;
  }
): Promise<IEventGameStats> {
  if (eventId === "demo") {
    await sleep(2000);
    return withLocation(() => $fetch<IResponseEventStatsTeams>("/mocks/data/event/123/stats_game.json"), formatGameStatsV3);
  }

  return withLocation(
    () =>
      useAPI<IResponseEventStatsTeams>(`/api/v3/events/${eventId}/statistics/teams`, {
        headers: getSharedHeaders(options),
        ...(options?.query && { query: options.query }),
      }),
    formatGameStatsV3
  );
}

export async function getEventScoreStatsById(
  eventId: string,
  options?: {
    query?: Partial<{ format: TEventStatsFormat; sheetId: number }>;
    accessToken?: string;
    locale?: string;
  }
): Promise<IEventScoreStats> {
  if (eventId === "demo") {
    await sleep(2000);
    return withLocation(() => $fetch<IResponseEventStatsScores>("/mocks/data/event/123/stats_score.json"), formatScoreStats);
  }

  return withLocation(
    () =>
      useAPI<IResponseEventStatsScores>(`/api/v3/events/${eventId}/statistics/scores`, {
        headers: getSharedHeaders(options),
        ...(options?.query && { query: options.query }),
      }),
    formatScoreStats
  );
}

export const getCachedEventAthletesStatsById = useMemoize(
  (
    eventId: string,
    options?: {
      query: Partial<{ format: "sheet"; sheetId: number }>;
      accessToken?: string;
      locale?: string;
    }
  ): Promise<IEventAthleteStats> => getEventAthletesStatsById(eventId, options)
);

export async function getEventAthletesStatsById(
  eventId: string,
  options?: {
    query: Partial<{ format: "sheet"; sheetId: number }>;
    accessToken?: string;
    locale?: string;
  }
): Promise<IEventAthleteStats> {
  if (eventId === "demo") {
    await sleep(2000);
    return withLocation(() => $fetch<IResponseEventStatsRosters>("/mocks/data/event/123/stats_athletes.json"), formatAthleteStats);
  }

  return withLocation(
    () =>
      useAPI<IResponseEventStatsRosters>(`/api/v1/events/${eventId}/statistics/rosters`, {
        headers: getSharedHeaders(options),
        ...(options?.query && { query: options.query }),
      }),
    formatAthleteStats
  );
}

export const getCachedEventAthletesStatsByIdV3 = useMemoize(
  (
    eventId: string,
    options?: {
      query: Partial<{ format: TEventStatsFormat; sheetId: number }>;
      accessToken?: string;
      locale?: string;
    }
  ): Promise<IEventAthleteStatsV3> => getEventAthletesStatsByIdV3(eventId, options)
);

export async function getEventAthletesStatsByIdV3(
  eventId: string,
  options?: {
    query: Partial<{ format: TEventStatsFormat; sheetId: number }>;
    accessToken?: string;
    locale?: string;
  }
): Promise<IEventAthleteStatsV3> {
  if (eventId === "demo") {
    await sleep(2000);
    return withLocation(() => $fetch<IResponseEventStatsRostersV3>("/mocks/data/event/123/stats_athletes.json"), formatAthleteStatsV3);
  }

  return withLocation(
    () =>
      useAPI<IResponseEventStatsRostersV3>(`/api/v3/events/${eventId}/statistics/rosters`, {
        headers: getSharedHeaders(options),
        ...(options?.query && { query: options.query }),
      }),
    formatAthleteStatsV3
  );
}

export const getCachedTeamStatsTypeOptionsByEventId = useMemoize(
  (eventId: string, options?: { accessToken?: string; locale?: string }): Promise<{ id: number; label: string; value: number }[]> =>
    getTeamStatsTypeOptionsByEventId(eventId, options)
);

export async function getTeamStatsTypeOptionsByEventId(
  eventId: string,
  options?: { accessToken?: string; locale?: string }
): Promise<{ id: number; label: string; value: number }[]> {
  if (eventId === "demo") return await new Promise((resolve) => setTimeout(() => resolve([]), 2000));

  // type: "rosters" | "teams"
  // return useAPI<IResponseEventStatsRosters>(`/v1/events/${eventId}/statistics/:type/sheets`, {
  return useAPI<{
    data: {
      id: number;
      type: "sheet";
      attributes: { name: string; type: "teams" };
    }[];
  }>(`/api/v1/events/${eventId}/statistics/teams/sheets`, {
    headers: getSharedHeaders(options),
  }).then((res) => res.data.map((i) => ({ id: i.id, label: i.attributes.name, value: i.id })));
}

export const getCachedAthleteStatsTypeOptionsByEventId = useMemoize(
  (eventId: string, options?: { accessToken?: string; locale?: string }): Promise<{ id: number; label: string; value: number }[]> =>
    getAthleteStatsTypeOptionsByEventId(eventId, options)
);

export async function getAthleteStatsTypeOptionsByEventId(
  eventId: string,
  options?: { accessToken?: string; locale?: string }
): Promise<{ id: number; label: string; value: number }[]> {
  if (eventId === "demo") return await new Promise((resolve) => setTimeout(() => resolve([]), 2000));

  // type: "rosters" | "teams"
  // return useAPI<IResponseEventStatsRosters>(`/v1/events/${eventId}/statistics/:type/sheets`, {
  return useAPI<{
    data: {
      id: number;
      type: "sheet";
      attributes: { name: string; type: "rosters" };
    }[];
  }>(`/api/v1/events/${eventId}/statistics/rosters/sheets`, {
    headers: getSharedHeaders(options),
  }).then((res) => res.data.map((i) => ({ id: i.id, label: i.attributes.name, value: i.id })));
}

export async function getMatchLeadersByEventId(eventId: string, options?: { accessToken?: string; locale?: string }): Promise<{ data: IMatchRosterLeaders }> {
  return useAPI(`api/v3/events/${eventId}/statistics/match-roster-leaders`, {
    headers: getSharedHeaders(options),
  });
}

export async function getAthleteStatsForMaxPrepsExport(
  eventId: string,
  teamId: string,
  season: string,
  options?: { accessToken?: string; locale?: string }
): Promise<{ data: string }> {
  return useAPI(`api/v1/events/${eventId}/statistics/rosters/maxpreps`, {
    query: {
      teamId,
      season,
    },
    headers: {
      ...getSharedHeaders(options),
      "Content-type": "text/plain",
    },
  });
}
